import { useEffect, useState } from "react";
import * as api from '../../api/index.js';
import { Button, Form, Modal, Alert } from 'react-bootstrap';
import axios from "axios";

export default function AddVideoDialog({ videos, setVideos, showDialog, setShowDialog }) {
    const CATEGORIES = {
        "Health": [
            "Nutrition",
            "Hydration"
        ],
        "Exercise": [
            "Walking Techniques",
            "Injury Prevention",
            "Stretching",
            "Body Weight Exercise",
            "Climate"
        ]
    };

    const [videoName, setVideoName] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [showLinkMessage, setShowLinkMessage] = useState(false);
    const [videoCategory, setVideoCategory] = useState(Object.keys(CATEGORIES)[0]);
    const [videoSubcategory, setVideoSubcategory] = useState(CATEGORIES[videoCategory][0]);
    const [validateForm, setValidateForm] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        if (showDialog) {
            setSubmitError(null);
            setValidateForm(false);
        }
    }, [showDialog]);

    const youtubeUrlRegexes = [
        /youtube\.com\/watch\?v=([\d\w]+)/i,
        /youtu\.be\/([\d\w]+)/i,
        /youtube\.com\/shorts\/([\d\w]+)/i
    ];

    function onVideoUrlChange(e) {
        /** @type string */
        const url = e.target.value.trim();
        setVideoUrl(url);
        setShowLinkMessage(e.target?.validity?.valid && youtubeUrlRegexes.some(r => url.match(r)));
    }

    /**
     * @param {string} url 
     * @returns {string}
     */
    function normalizeVideoUrl(url) {
        if (!showLinkMessage) {
            return url;
        }

        for (let i = 0; i < youtubeUrlRegexes.length; i++) {
            const matches = url.match(youtubeUrlRegexes[i]);
            if (matches) {
                return `https://youtube.com/embed/${matches[1]}`;
            }
        }
    }

    async function createVideo(e) {
        e.preventDefault();
        e.stopPropagation();
        setValidateForm(true);

        if (!e.currentTarget.checkValidity()) {
            return;
        }

        try {
            const { data: video } = await api.createVideo({ title: videoName, url: normalizeVideoUrl(videoUrl), category: videoCategory, subCategory: videoSubcategory });
            setVideos([video, ...videos]);
            setVideoName("");
            setVideoUrl("");
            setShowLinkMessage(false);
            
            setShowDialog(false);
            setSubmitError(null);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                setSubmitError({
                    "server": "An unexpected error occurred.",
                    "duplicate-field-title": "A video with this title already exists.",
                    "duplicate-field-url": "A video with this link already exists."
                }[error.response.data.errorType]);
            } else {
                setSubmitError("An unexpected error occurred.");
            }
        }
    }

    function changeCategory(e) {
        const newCategory = e.target.value;
        setVideoCategory(newCategory);
        setVideoSubcategory(CATEGORIES[newCategory][0]);
    }

    return (
        <Modal show={showDialog} onHide={() => setShowDialog(false)}>
            <Form noValidate validated={validateForm} onSubmit={createVideo}>
                <Modal.Header closeButton>
                    <Modal.Title>Add Video Link</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="videoName">Video Name</Form.Label>
                        <Form.Control id="videoName" type="text" required value={videoName} onChange={e => setVideoName(e.target.value)} />
                        <Form.Control.Feedback type="invalid">
                            Please enter a video name.
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="videoLink">Video Link</Form.Label>
                        <Form.Control id="videoLink" type="url" required value={videoUrl} onChange={onVideoUrlChange} />
                        <Form.Control.Feedback type="invalid">
                            Please enter a valid URL.
                        </Form.Control.Feedback>
                        {showLinkMessage && <Form.Control.Feedback className="d-block" variant="info">
                            YouTube link detected. This link will automatically be converted to the corresponding embed link.
                        </Form.Control.Feedback>}
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="videoCategory">Category</Form.Label>
                        <Form.Select id="videoCategory" className="no-validate" value={videoCategory} onChange={changeCategory}>
                            {Object.keys(CATEGORIES).map(category => <option key={category}>{category}</option>)}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label htmlFor="videoSubcategory">Subcategory</Form.Label>
                        <Form.Select id="videoSubcategory" className="no-validate" value={videoSubcategory} onChange={e => setVideoSubcategory(e.target.value)}>
                            {CATEGORIES[videoCategory].map(category => <option key={category}>{category}</option>)}
                        </Form.Select>
                    </Form.Group>
                    {submitError && <Alert variant="danger">{submitError}</Alert>}
                </Modal.Body>
                <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    <Button variant="danger" className="btn-cancel modal-action-btn" onClick={() => setShowDialog(false)}>Cancel</Button>
                    <Button type="submit" className="btn btn-blue modal-action-btn">Add</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}